import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const STATUS_COLORS = {
  proposed: '#50E3C2',
  approved: '#08B2E3',
  under_construction: '#F8E71C',
  completed: '#005E7C',
  cancelled: '#F93950',
};

export default function EmbedMap({ apiUrl, mapboxToken, mapboxStyle }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [projects, setProjects] = useState([]);
  const [center, setCenter] = useState(null);

  // Fetch project data from the same API the main map uses.
  useEffect(() => {
    if (!apiUrl) return;
    let cancelled = false;

    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setProjects(data.projects || []);
        setCenter(data.center || null);
      })
      .catch((err) => console.error('Homepage map: failed to load projects', err));

    return () => { cancelled = true; };
  }, [apiUrl]);

  // Initialize the map once we have the token and a center.
  useEffect(() => {
    if (!mapboxToken || !center || map.current) return;

    mapboxgl.accessToken = mapboxToken;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapboxStyle || 'mapbox://styles/mapbox/light-v11',
      center: [center.lon, center.lat],
      zoom: 11,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
    });

    map.current.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      'bottom-right'
    );

    map.current.on('load', () => {
      addLayers();
      setupHandlers();
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapboxToken, mapboxStyle, center]);

  // Update the GeoJSON source when projects change.
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;
    const source = map.current.getSource('projects');
    if (source) {
      source.setData(buildGeoJSON(projects));
    }
  }, [projects]);

  function buildGeoJSON(items) {
    return {
      type: 'FeatureCollection',
      features: items.map((p) => ({
        type: 'Feature',
        properties: { nid: p.nid, status: p.status },
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
      })),
    };
  }

  function addLayers() {
    if (!map.current) return;

    map.current.addSource('projects', {
      type: 'geojson',
      data: buildGeoJSON(projects),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    map.current.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step', ['get', 'point_count'],
          '#08B2E3', 10, '#50E3C2', 50, '#005E7C',
        ],
        'circle-radius': [
          'step', ['get', 'point_count'],
          20, 10, 25, 50, 35,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    map.current.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'projects',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: { 'text-color': '#fff' },
    });

    map.current.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'match', ['get', 'status'],
          'proposed', STATUS_COLORS.proposed,
          'approved', STATUS_COLORS.approved,
          'under_construction', STATUS_COLORS.under_construction,
          'completed', STATUS_COLORS.completed,
          'cancelled', STATUS_COLORS.cancelled,
          '#08B2E3',
        ],
        'circle-radius': 10,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });
  }

  function setupHandlers() {
    if (!map.current) return;

    // Cluster click → zoom in
    map.current.on('click', 'clusters', (e) => {
      const feature = e.features[0];
      map.current.getSource('projects').getClusterExpansionZoom(
        feature.properties.cluster_id,
        (err, zoom) => {
          if (err) return;
          map.current.easeTo({
            center: feature.geometry.coordinates,
            zoom,
          });
        }
      );
    });

    // Pin click → redirect to full map with project deep-link
    map.current.on('click', 'unclustered-point', (e) => {
      const nid = e.features[0].properties.nid;
      window.location.href = '/map?project=' + nid;
    });

    map.current.on('mouseenter', 'clusters', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = '';
    });
    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = '';
    });
  }

  return <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />;
}
